// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Component, OnInit } from '@angular/core';
import { HeaderService } from 'src/app/header.service';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { IIntegration, PartnerTypes } from 'src/types/integrations';
import { AlertDialogComponent } from 'src/app/dialogs/alert/alert.component';
import { ActivatedRoute, Router } from '@angular/router';
import { D3eService } from 'src/services/d3e-service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from 'src/services/notification-service';
import {SelectedOrgMethod} from '../../../../shared/selectedOrg';
import {TranslateService} from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

function validateThreatNumber(control: UntypedFormControl) {
  if (+control.value < 1501 || +control.value > 1599) {
    return {
      validatePort: {
        valid: false
      }
    };
  }
  return null;
}

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-third-party',
    templateUrl: './third-party.component.html',
    styleUrls: ['./third-party.component.scss'],
    standalone: false
})
export class ThirdPartyComponent implements OnInit {
  private readonly PARTNER_ID = 0;
  formGroup: UntypedFormGroup;
  integration: IIntegration;
  integrationId: string = null;
  token: string = null;

  selectedOrgId: string;
  hasUserBeenSet = false;

  fName: string;
  fThreatNumb: string;
  fThreatNumbPHolder: string;
  fDesc: string;
  fDescPHolder: string;
  fSave: string;
  fCancel: string;
  fInvalidate: string;
  fConfigSaved: string;
  fChangesBeingSaved: string;
  fSaveError: string;

  title: string;
  constructor(
    private route: ActivatedRoute,
    private d3eService: D3eService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private notification: NotificationService,
    public dialog: MatDialog,
    private headerService: HeaderService,
    private translate: TranslateService
  ) {
    this.translate.get(['NAME', 'INTT_THREAT_NUMB', 'INTT_THREAT_PHOLDER', 'INTT_DESC',
      'INTT_DESC_PHOLDER', 'SAVE', 'CANCEL', 'INTT_INVALIDATAE', 'TITLE_THIRD_INT', 'CONFIG_SAVED',
      'CHANGES_BEING_SAVED', 'P_UPDATING_POLICY', 'REPORT_SAVE_ERROR']).subscribe(vals => {
        this.fName = vals['NAME'];
        this.fThreatNumb = vals['INTT_THREAT_NUMB'];
        this.fThreatNumbPHolder = vals['INTT_THREAT_PHOLDER'];
        this.fDesc = vals['INTT_DESC'];
        this.fDescPHolder = vals['INTT_DESC_PHOLDER'];
        this.fSave = vals['SAVE'];
        this.fCancel = vals['CANCEL'];
        this.fInvalidate = vals['INTT_INVALIDATAE'];
        this.fConfigSaved = vals['CONFIG_SAVED'];
        this.fChangesBeingSaved = vals['CHANGES_BEING_SAVED'];
        this.title = vals['TITLE_THIRD_INT'];
        this.fSaveError = vals['REPORT_SAVE_ERROR'];

        this.headerService.setTitle(this.title);
    });
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-sync');

    this.formGroup = this.formBuilder.group({
      display_name: ['', Validators.required],
      sensor_type: ['', [Validators.required, validateThreatNumber]],
      description: ['', Validators.required],
    });
  }

  ngOnInit() {

    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        if ( this.hasUserBeenSet ) {
            // TODO: launch the dirty flag window
            this.router.navigateByUrl('/integrations');
        }
        this.hasUserBeenSet = true;
    });

    this.route.params.subscribe(params => {
      this.integrationId = params.id;
      if (this.integrationId === 'new') {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        this.d3eService.getSensorTypes(selectedOrg)
          .pipe(untilDestroyed(this))
          .subscribe(response => {
            const bySensorType = response.sensorTypes.reduce((a, v) => {
              a[v.value] = v;
              return a;
            }, {});
            for (let i = 1501; i < 1600; i++) {
              if (bySensorType[i] === undefined) {
                this.formGroup.controls['sensor_type'].setValue(i);
                return;
              }
            }
        });
      } else {
        const selectedOrgId = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        this.d3eService.get_integration(this.integrationId, selectedOrgId)
          .pipe(untilDestroyed(this))
          .subscribe(integration => {
            if (integration.partner_id !== this.PARTNER_ID) {
              this.router.navigateByUrl('/integrations');
            }
            this.formGroup.controls['display_name'].setValue(integration.display_name);
            this.formGroup.controls['sensor_type'].setValue(integration.config.sensorType);
            this.formGroup.controls['description'].setValue(integration.config.description);
            this.integration = integration;
          });
        this.d3eService.get_integration_token(this.integrationId, false, selectedOrgId)
          .pipe(untilDestroyed(this))
          .subscribe(response => {
            this.token = response.token;
        });
      }
    });
  }

  save() {
    this.notification.showUpdate(this.fChangesBeingSaved);
    const data = this.formGroup.value;
    if (this.integrationId === 'new') {
      const integration: IIntegration = {
        display_name: data.display_name,
        enabled: false,
        partner_id: PartnerTypes.THIRD_PARTY,
        config: {
          sensorType: Number(data.sensor_type),
          description: data.description,
        }
      };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
      this.d3eService.add_integration(integration, selectedOrg)
        .subscribe(result => {
          this.reportSaveSuccess();
          this.router.navigateByUrl('/third-party/' + result.new_id);
        }, this.reportSaveError.bind(this));
    } else {
      this.integration.display_name = data.display_name;
      this.integration.config = {
        sensorType: Number(data.sensor_type),
        description: data.description,
      };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.update_integration(this.integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    }
  }

  reportSaveSuccess() {
    this.notification.showSuccess(this.fConfigSaved);
  }

  reportSaveError(error) {
    this.notification.showError(this.fSaveError);
  }

  goToIntegrations() {
    this.router.navigateByUrl('/integrations');
  }

  cancel() {
    if (this.formGroup.touched) {
      const vals = this.translate.instant(['DISCARD_CHANGES', 'DISCARD_CHANGES_CONTENT', 'YES', 'NO']);
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title: vals['DISCARD_CHANGES'],
          content: vals['DISCARD_CHANGES_CONTENT'],
          rejectBtnName: vals['NO'],
          acceptBtnName: vals['YES'],
          showRejectBtn: true,
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.goToIntegrations();
        }
      });
    } else {
      this.goToIntegrations();
    }
  }

  invalidateSecret() {
    const vals = this.translate.instant(['INVALIDATE_EXISTING_TOKENS_TITLE',
      'INVALIDATE_EXISTING_TOKENS_CONTENT', 'YES', 'NO']);
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '450px',
      data: {
        title: vals['INVALIDATE_EXISTING_TOKENS_TITLE'],
        content: vals['INVALIDATE_EXISTING_TOKENS_CONTENT'],
        rejectBtnName: vals['NO'],
        acceptBtnName: vals['YES'],
        showRejectBtn: true,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.d3eService.get_integration_token(this.integrationId, true)
          .pipe(untilDestroyed(this))
          .subscribe(response => {
            this.token = response.token;
        });
      }
    });
  }

}
